import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Send, X, CircleDot, Clock, User, ChevronLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const WS_BASE = API.replace('https://', 'wss://').replace('http://', 'ws://');

export default function AdminChat() {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const messagesEnd = useRef(null);
  const pollRef = useRef(null);

  const getAdminToken = useCallback(() => {
    return localStorage.getItem('adminToken') || null;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchChats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/chat/all`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setChats(d.chats || []); }
    } catch (e) { console.error("Chat error:", e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchChats();
    pollRef.current = setInterval(fetchChats, 5000);
    return () => clearInterval(pollRef.current);
  }, [fetchChats]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const openChat = async (chat) => {
    setSelectedChat(chat);
    setMessages([]);

    // Fetch history first
    try {
      const res = await fetch(`${API}/api/chat/history/${chat.id}`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setMessages(d.messages || []); }
    } catch (e) { console.error("Chat error:", e); }

    // Connect WebSocket
    if (ws) { ws.close(); setWs(null); }
    const token = getAdminToken();
    if (!token) return;

    const socket = new WebSocket(`${WS_BASE}/api/chat/admin?token=${token}&chat_id=${chat.id}`);
    socket.onmessage = (evt) => {
      const data = JSON.parse(evt.data);
      if (data.type === 'init') {
        setMessages(data.messages || []);
      } else if (data.type === 'message') {
        setMessages(prev => [...prev, data]);
      } else if (data.type === 'chat_closed') {
        setSelectedChat(prev => prev ? { ...prev, status: 'closed' } : null);
      }
    };
    socket.onerror = () => {};
    socket.onclose = () => {};
    setWs(socket);
  };

  const sendMessage = () => {
    if (!input.trim() || !ws || ws.readyState !== 1) return;
    ws.send(JSON.stringify({ type: 'message', message: input.trim() }));
    setInput('');
  };

  const closeChat = () => {
    if (ws && ws.readyState === 1) {
      ws.send(JSON.stringify({ type: 'close_chat' }));
    }
  };

  const backToList = () => {
    if (ws) { ws.close(); setWs(null); }
    setSelectedChat(null);
    setMessages([]);
    fetchChats();
  };

  const activeChats = chats.filter(c => c.status === 'active');
  const closedChats = chats.filter(c => c.status === 'closed');

  return (
    <div className="max-w-6xl mx-auto" data-testid="admin-chat-page">
      <h1 className="text-2xl font-bold text-white mb-6">Live Chat</h1>

      <div className="flex gap-4 h-[calc(100vh-200px)] min-h-[500px]">
        {/* Chat List */}
        <div className={`w-80 shrink-0 bg-[#0E1830] border border-white/[0.08] rounded-2xl overflow-hidden flex flex-col ${selectedChat ? 'hidden lg:flex' : 'flex'}`}>
          <div className="p-4 border-b border-white/[0.06]">
            <h2 className="text-white font-bold text-sm">Conversations</h2>
            <p className="text-white/30 text-xs mt-1">{activeChats.length} active &middot; {closedChats.length} closed</p>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-white/30 text-sm">Loading...</div>
            ) : chats.length === 0 ? (
              <div className="p-6 text-center">
                <MessageSquare className="w-8 h-8 text-white/10 mx-auto mb-2" />
                <p className="text-white/30 text-sm">No conversations yet</p>
              </div>
            ) : (
              <>
                {activeChats.length > 0 && (
                  <div className="p-2">
                    <p className="px-2 py-1 text-[10px] text-white/25 uppercase tracking-wider font-bold">Active</p>
                    {activeChats.map(c => (
                      <button key={c.id} onClick={() => openChat(c)}
                        className={`w-full text-left p-3 rounded-xl transition-all ${selectedChat?.id === c.id ? 'bg-[#8B5CF6]/15 border border-[#8B5CF6]/30' : 'hover:bg-white/[0.04]'}`}
                        data-testid={`chat-${c.id}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <CircleDot className="w-3 h-3 text-emerald-400" />
                          <span className="text-white text-xs font-bold truncate">{c.user_name || c.user_email || 'Anonymous'}</span>
                          {c.online && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
                        </div>
                        <p className="text-white/30 text-[10px] truncate">{c.subject || 'No subject'}</p>
                        {c.unread > 0 && <span className="text-[9px] bg-[#8B5CF6] text-white px-1.5 py-0.5 rounded-full ml-1">{c.unread}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {closedChats.length > 0 && (
                  <div className="p-2 border-t border-white/[0.04]">
                    <p className="px-2 py-1 text-[10px] text-white/25 uppercase tracking-wider font-bold">Closed</p>
                    {closedChats.slice(0, 10).map(c => (
                      <button key={c.id} onClick={() => openChat(c)}
                        className="w-full text-left p-3 rounded-xl hover:bg-white/[0.04] transition-all" data-testid={`chat-closed-${c.id}`}>
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3 text-white/20" />
                          <span className="text-white/40 text-xs truncate">{c.user_name || 'Anonymous'}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Chat Window */}
        <div className={`flex-1 bg-[#0E1830] border border-white/[0.08] rounded-2xl overflow-hidden flex flex-col ${!selectedChat ? 'hidden lg:flex' : 'flex'}`}>
          {selectedChat ? (
            <>
              <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button onClick={backToList} className="lg:hidden text-white/40 hover:text-white"><ChevronLeft className="w-5 h-5" /></button>
                  <User className="w-8 h-8 p-1.5 rounded-full bg-white/[0.06] text-white/40" />
                  <div>
                    <h3 className="text-white font-bold text-sm">{selectedChat.user_name || 'Anonymous'}</h3>
                    <p className="text-white/30 text-[10px]">{selectedChat.subject || 'Support chat'} &middot; {selectedChat.status}</p>
                  </div>
                </div>
                {selectedChat.status === 'active' && (
                  <Button onClick={closeChat} variant="outline" className="text-red-400 border-red-400/30 hover:bg-red-400/10 text-xs rounded-full px-4" data-testid="close-chat-btn">
                    <X className="w-3 h-3 mr-1" /> Close
                  </Button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
                {messages.map((m, i) => (
                  <div key={`${m.created_at}-${m.sender_type}-${i}`} className={`flex ${m.sender_type === 'agent' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm ${
                      m.sender_type === 'agent'
                        ? 'bg-[#8B5CF6]/20 text-white rounded-br-md'
                        : 'bg-white/[0.06] text-white/80 rounded-bl-md'
                    }`}>
                      <p className="whitespace-pre-wrap">{m.message}</p>
                      <p className="text-[9px] text-white/20 mt-1">{new Date(m.created_at).toLocaleTimeString()}</p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEnd} />
              </div>

              {selectedChat.status === 'active' && (
                <div className="p-4 border-t border-white/[0.06]">
                  <div className="flex gap-2">
                    <input value={input} onChange={e => setInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())}
                      placeholder="Type a reply..."
                      className="flex-1 px-4 py-2.5 bg-white/[0.06] border border-white/[0.1] rounded-xl text-white text-sm focus:border-[#8B5CF6]/40 focus:outline-none placeholder:text-white/25"
                      data-testid="chat-input" />
                    <Button onClick={sendMessage} className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-xl px-4" data-testid="chat-send-btn">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 text-white/10 mx-auto mb-3" />
                <p className="text-white/30 text-sm">Select a conversation to reply</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
